import React, { useState } from "react";
import "./Careers.css";

const openings = [
  {
    title: "Marine Operations Coordinator",
    type: "Full-time",
    location: "Port Harcourt, Nigeria",
    description:
      "Coordinate vessel movements, field logistics and offshore support activities across active projects.",
  },
  {
    title: "Project Engineer",
    type: "Full-time",
    location: "Lagos, Nigeria",
    description:
      "Support engineering delivery, project planning and technical execution across energy and infrastructure assignments.",
  },
  {
    title: "HSE Officer",
    type: "Full-time",
    location: "Project locations",
    description:
      "Embed practical safety, quality and environmental standards across field operations and project teams.",
  },
  {
    title: "Graduate Development Programme",
    type: "Early career",
    location: "Nigeria",
    description:
      "Build practical experience through structured exposure to engineering, marine operations and project delivery.",
  },
];

function Careers() {
  const [isApplicationOpen, setIsApplicationOpen] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionError, setSubmissionError] = useState("");
  const [selectedRole, setSelectedRole] = useState("");

  const openApplication = (role = "") => {
    setSelectedRole(role);
    setSubmissionError("");
    setIsApplicationOpen(true);
  };

  const closeApplication = () => {
    setIsApplicationOpen(false);
    setIsSubmitted(false);
    setIsSubmitting(false);
    setSubmissionError("");
    setSelectedRole("");
  };

  const handleApplicationSubmit = async (event) => {
    event.preventDefault();
    setIsSubmitting(true);
    setSubmissionError("");

    try {
      const response = await fetch("/api/apply.php", {
        method: "POST",
        body: new FormData(event.currentTarget),
      });
      const result = await response.json().catch(() => null);

      if (!response.ok || !result?.success) {
        throw new Error(result?.message || "We could not submit your application. Please try again.");
      }

      setIsSubmitted(true);
    } catch (error) {
      setSubmissionError(error.message || "We could not submit your application. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <main className="careers-page">
      <section className="careers-hero">
        <img
          className="careers-hero-image"
          src="/images/eclear.png"
          alt="Technical team member at work"
        />
        <div className="careers-hero-overlay" />
        <div className="careers-container careers-hero-content">
          <span className="careers-label">Careers</span>
          <h1>
            Build the work
            <br />
            <span>that moves progress.</span>
          </h1>
          <p>
            Join a practical, ambitious team delivering energy, maritime and
            industrial solutions across challenging environments.
          </p>
        </div>
      </section>

      <section className="careers-intro">
        <div className="careers-container careers-intro-grid">
          <div>
            <span className="careers-label">Life at Strides</span>
            <h2>
              Your expertise can make
              <br />
              <span>an everyday difference.</span>
            </h2>
          </div>
          <div className="careers-intro-copy">
            <p>
              We bring together people who care about doing useful work well.
              Our teams collaborate across engineering, operations, marine
              logistics and commercial delivery to solve real project challenges.
            </p>
            <p>
              Whether you are starting your career or bringing years of field
              experience, there is room to learn, contribute and grow with us.
            </p>
          </div>
        </div>
        <div className="careers-container">
          <figure className="careers-life-image">
            <img
              src="/images/photos/Briefing by the Water.webp"
              alt="Field personnel receiving a safety briefing beside the water"
              loading="lazy"
            />
          </figure>
        </div>
      </section>

      <section className="careers-openings">
        <div className="careers-container">
          <div className="careers-section-heading">
            <div>
              <span className="careers-label">Open opportunities</span>
              <h2>
                Find your next
                <br />
                <span>challenge.</span>
              </h2>
            </div>
            <p>
              Explore roles where your skills can support safer operations,
              stronger projects and lasting client value.
            </p>
          </div>

          <div className="careers-openings-grid">
            {openings.map((opening) => (
              <article className="career-card" key={opening.title}>
                <div className="career-card-top">
                  <span>{opening.type}</span>
                  <span>Open role</span>
                </div>
                <h3>{opening.title}</h3>
                <p>{opening.description}</p>
                <div className="career-card-footer">
                  <span>{opening.location}</span>
                  <button type="button" onClick={() => openApplication(opening.title)} aria-label={`Apply for ${opening.title}`}>
                    Apply <span aria-hidden="true">→</span>
                  </button>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="careers-cta">
        <div className="careers-container careers-cta-inner">
          <div>
            <span className="careers-label">No exact match?</span>
            <h2>
              Tell us where
              <br />
              <span>you can add value.</span>
            </h2>
          </div>
          <button
            type="button"
            className="careers-cta-button"
            onClick={() => openApplication()}
          >
            Send your CV <span aria-hidden="true">→</span>
          </button>
        </div>
      </section>

      {isApplicationOpen && (
        <div
          className="career-application-overlay"
          onClick={(event) => {
            if (event.target === event.currentTarget) {
              closeApplication();
            }
          }}
        >
          <div className="career-application-modal" role="dialog" aria-modal="true" aria-labelledby="career-application-title">
            <button
              type="button"
              className="career-application-close"
              onClick={closeApplication}
              aria-label="Close application form"
            >
              ×
            </button>

            {isSubmitted ? (
              <div className="career-application-success">
                <span className="careers-label">Application received</span>
                <h2>Thank you for your interest in Strides.</h2>
                <p>
                  Your application has been sent to our team. We will review it
                  and get back to you.
                </p>
                <button type="button" className="career-application-submit" onClick={closeApplication}>
                  Close
                </button>
              </div>
            ) : (
              <>
                <span className="careers-label">Join the team</span>
                <h2 id="career-application-title">Send your CV</h2>
                <p className="career-application-intro">
                  Tell us about yourself and upload your CV for consideration.
                </p>

                <form className="career-application-form" onSubmit={handleApplicationSubmit}>
                  <div className="career-application-honeypot" aria-hidden="true">
                    <label>
                      Leave this field empty
                      <input type="text" name="website" tabIndex="-1" autoComplete="off" />
                    </label>
                  </div>

                  <label>
                    Full name
                    <input type="text" name="fullName" required />
                  </label>

                  <label>
                    Email address
                    <input type="email" name="email" required />
                  </label>

                  <label>
                    Phone number
                    <input type="tel" name="phone" required />
                  </label>

                  <label>
                    Role of interest
                    <select name="role" defaultValue={selectedRole} required>
                      <option value="" disabled>Select a role</option>
                      {openings.map((opening) => (
                        <option value={opening.title} key={opening.title}>
                          {opening.title}
                        </option>
                      ))}
                      <option value="General application">General application</option>
                    </select>
                  </label>

                  <label className="career-application-full-width">
                    Brief introduction
                    <textarea name="message" rows="4" placeholder="Tell us about your experience and how you can contribute." />
                  </label>

                  <label className="career-application-full-width">
                    Upload your CV
                    <input type="file" name="cv" accept=".pdf,.doc,.docx" required />
                    <span className="career-application-file-note">PDF, DOC or DOCX, maximum 10 MB</span>
                  </label>

                  {submissionError && (
                    <p className="career-application-error" role="alert">
                      {submissionError}
                    </p>
                  )}

                  <button type="submit" className="career-application-submit" disabled={isSubmitting}>
                    {isSubmitting ? "Submitting..." : "Submit application"}
                    {!isSubmitting && <span aria-hidden="true">→</span>}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      )}
    </main>
  );
}

export default Careers;
